import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DashboardProvider } from './context/DashboardContext';
import { ShellProvider, useShell } from './context/ShellContext';
import { UserRole } from './types';
import LoginPage from './pages/LoginPage';
import TeacherDashboard from './pages/TeacherDashboard';
import OrcaLexDashboard from './pages/OrcaLexDashboard';
import T from './theme/tokens';

// The school leadership dashboard ships its own animation stack; load it only for school admins.
const SchoolDashboard = lazy(() => import('./pages/SchoolDashboard'));
// import ChatBot from './components/ChatBot';

const FONT = T.font.sans;

/* ─── Route guard ────────────────────────────────────────────── */
const ProtectedRoute: React.FC<{
  children: React.ReactElement;
  requiredRole?: UserRole;
}> = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1120', color: '#F1F5F9', display: 'grid', placeItems: 'center' }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (requiredRole && user?.role !== requiredRole) {
    if (user?.role === UserRole.ORCALEX_ADMIN) return <Navigate to="/orcalex" replace />;
    if (user?.role === UserRole.SCHOOL_ADMIN) return <Navigate to="/school" replace />;
    return <Navigate to="/teacher" replace />;
  }
  return children;
};

/* ─── Scroll-to-top FAB ──────────────────────────────────────── */
const ScrollToTop: React.FC = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
      className="sl-btn sl-btn--secondary"
      style={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 40,
        width: 40, height: 40, padding: 0, borderRadius: 12,
        boxShadow: T.shadow.lg, animation: 'sl-fade-in-up 200ms both',
      }}
    >
      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24" aria-hidden>
        <path d="M18 15l-6-6-6 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
};

/* ─── Top bar / dashboard layout ─────────────────────────────── */
const Icon = {
  menu: <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />,
  close: <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />,
  bell: <><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" /><path d="M13.7 21a2 2 0 0 1-3.4 0" strokeLinecap="round" /></>,
  chevron: <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" strokeLinecap="round" strokeLinejoin="round" /><polyline points="16 17 21 12 16 7" strokeLinecap="round" strokeLinejoin="round" /><line x1="21" y1="12" x2="9" y2="12" strokeLinecap="round" /></>,
};

const Svg: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>{children}</svg>
);

const DashboardLayout: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { navOpen, setNavOpen, hasSidebar, chromeHidden } = useShell();
  const [menuOpen, setMenuOpen] = useState(false);

  const roleMeta = (() => {
    if (user?.role === UserRole.ORCALEX_ADMIN) return { label: 'Platform Admin', short: 'Admin' };
    if (user?.role === UserRole.SCHOOL_ADMIN) return { label: 'School Admin', short: 'School' };
    return { label: 'Teacher', short: 'Teacher' };
  })();

  const initials = (user?.full_name || user?.username || 'U')
    .split(/\s+|@/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('');

  return (
    <div className="sl-dashboard" style={{ minHeight: '100vh', background: T.surface.canvas, fontFamily: FONT }}>
      {!chromeHidden && <header className="sl-topbar">
        <div className="sl-topbar__inner">
          <div className="sl-topbar__left">
            {hasSidebar && (
              <button
                className="sl-icon-btn sl-icon-btn--menu"
                onClick={() => setNavOpen(!navOpen)}
                aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={navOpen}
              >
                <Svg size={20}>{navOpen ? Icon.close : Icon.menu}</Svg>
              </button>
            )}
            <span className="sl-wordmark">Smartlearners<b>.ai</b></span>
          </div>

          <div className="sl-topbar__right">
            <button className="sl-icon-btn" aria-label="Notifications" title="No new notifications">
              <Svg>{Icon.bell}</Svg>
            </button>
            <span className="sl-topbar__divider" aria-hidden />
            <div style={{ position: 'relative' }}>
              <button
                className="sl-user-btn"
                onClick={() => setMenuOpen((v) => !v)}
                onBlur={() => setTimeout(() => setMenuOpen(false), 120)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Account menu"
              >
                <span aria-hidden className="sl-avatar">{initials}</span>
                <span className="sl-user-meta">
                  <strong>{user?.full_name || user?.username || 'Guest'}</strong>
                  <span>{roleMeta.label}</span>
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text.tertiary} strokeWidth="2.2" aria-hidden style={{ transition: 'transform 150ms', transform: menuOpen ? 'rotate(180deg)' : 'none' }}>
                  {Icon.chevron}
                </svg>
              </button>

              {menuOpen && (
                <div role="menu" className="sl-menu">
                  <div className="sl-menu__head">
                    <span aria-hidden className="sl-avatar" style={{ width: 36, height: 36 }}>{initials}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name || user?.username}</div>
                      <div style={{ fontSize: 11.5, color: T.text.tertiary, marginTop: 2 }}>{roleMeta.label}</div>
                    </div>
                  </div>
                  <button
                    role="menuitem"
                    className="sl-menu__item sl-menu__item--danger"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={logout}
                  >
                    <Svg size={16}>{Icon.logout}</Svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>}

      {children}

      {/* <ChatBot /> */}
      {!chromeHidden && <ScrollToTop />}
    </div>
  );
};

/* ─── Router ─────────────────────────────────────────────────── */
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1120', color: '#F1F5F9', display: 'grid', placeItems: 'center' }}>
        Loading...
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/teacher"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TeacherDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/school"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div style={{ minHeight: '100vh', background: '#EEF0F7' }} />}>
                <SchoolDashboard />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/orcalex"
        element={
          <ProtectedRoute requiredRole={UserRole.ORCALEX_ADMIN}>
            <DashboardLayout>
              <OrcaLexDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/"
        element={
          isAuthenticated ? (
            user?.role === UserRole.ORCALEX_ADMIN ? <Navigate to="/orcalex" replace /> :
            user?.role === UserRole.SCHOOL_ADMIN ? <Navigate to="/school" replace /> :
            <Navigate to="/teacher" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <DashboardProvider>
        <ShellProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ShellProvider>
      </DashboardProvider>
    </AuthProvider>
  );
};

export default App;
